import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, Clock, Ticket, MapPin, Sparkles, Bell, Repeat } from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_COLORS } from "@/components/CategoryIcon";
import { useAuth } from "@/context/AuthContext";
import { recurrenceLabel, parseEventDate } from "@/lib/events";
import api from "@/lib/api";

export function formatEventDate(iso) {
  const d = parseEventDate(iso);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
export function formatEventTime(iso) {
  return parseEventDate(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export function EventCard({ event, index = 0 }) {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const color = CATEGORY_COLORS[event.category] || "#7A6A55";
  const saved = user && user.saved_events?.includes(event.id);

  const toggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return toast.error("Sign in to save events & get reminders");
    await api.post(`/favorites/events/${event.id}`);
    await refreshUser();
    toast.success(saved ? "Reminder removed" : "Saved! We'll email you a reminder before this event.");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.35) }}
      onClick={() => navigate(`/event/${event.id}`)}
      data-testid={`event-card-${event.id}`}
      className="card-hover group relative flex cursor-pointer gap-4 overflow-hidden rounded-2xl border border-border bg-card p-3 shadow-sm"
    >
      <button onClick={toggleSave} data-testid={`event-save-${event.id}`}
        className={`absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border transition-transform hover:scale-110 ${saved ? "border-secondary bg-secondary text-secondary-foreground" : "border-border bg-card/90 text-foreground backdrop-blur-sm"}`}
        title={saved ? "Reminder on" : "Remind me"}>
        <Bell size={15} className={saved ? "fill-secondary-foreground" : ""} />
      </button>

      <div className="relative h-28 w-28 flex-shrink-0 overflow-hidden rounded-xl">
        <img src={event.cover_image} alt={event.title} loading="lazy" decoding="async"
             className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
        {event.is_sponsored && (
          <span className="absolute left-1 top-1 flex items-center gap-1 rounded-full bg-accent/90 px-1.5 py-0.5 text-[9px] font-bold text-accent-foreground">
            <Sparkles size={9} /> Promoted
          </span>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center pr-8">
        <span className="mb-1 inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[10px] font-semibold text-white"
              style={{ backgroundColor: `${color}dd` }}>
          {event.category}
        </span>
        <h3 className="line-clamp-1 font-serif text-lg font-bold text-foreground">{event.title}</h3>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><CalendarDays size={12} /> {formatEventDate(event.start_datetime)}</span>
          <span className="flex items-center gap-1"><Clock size={12} /> {formatEventTime(event.start_datetime)} ET</span>
          {event.venue_name && <span className="flex items-center gap-1"><MapPin size={12} /> {event.venue_name}</span>}
          {event.is_recurring && <span data-testid="event-recurrence-badge" className="flex items-center gap-1 font-medium text-secondary"><Repeat size={12} /> {recurrenceLabel(event)}</span>}
        </div>
        <div className="mt-1.5 flex items-center gap-3">
          <span className="text-xs font-semibold text-primary">
            {event.is_free ? "Free" : event.price_info || "Tickets available"}
          </span>
          {event.ticket_url && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(event.ticket_url, "_blank", "noopener"); }}
              data-testid={`event-tickets-${event.id}`}
              className="flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-primary-foreground transition-transform hover:scale-105">
              <Ticket size={11} /> Tickets
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
